/**
 * Declarative config of the premium documents panel
 * (`extranet.premiumDocumentsPanel`, fiche 22763): the per-document value
 * descriptors (stable keys `nom`, `categorieDate`), their text and the panel
 * texts.
 */

import type { InfoItemDescriptor } from '@components/shared/configurable';

import type { DocumentItem } from '../shared';

/** Registered id of the panel. */
export const PREMIUM_DOCUMENTS_PANEL_ID = 'extranet.premiumDocumentsPanel';

/** Token of the premium reference in the (overridable) panel title. */
export const NUM_QUI_TOKEN = '{numQui}';

export const PREMIUM_DOCUMENTS_PANEL_TEXT = {
  /**
   * Base title — « Documents — {NUM_QUI} ». A `setTitle` rule may keep the
   * `{numQui}` token to show the premium reference.
   */
  title: `Documents — ${NUM_QUI_TOKEN}`,
} as const;

/** Stable keys of a document's displayed values. */
export type PremiumDocumentItemKey = 'nom' | 'categorieDate';

/** A document value descriptor (override-able surface). */
export interface PremiumDocumentItemField extends InfoItemDescriptor {
  key: PremiumDocumentItemKey;
}

/**
 * Base descriptors: the name (primary line), then « catégorie · date »
 * (secondary line). No size, no « Voir » action (fiche: unspecified).
 */
export const PREMIUM_DOCUMENT_ITEM_FIELDS: readonly PremiumDocumentItemField[] = [
  { key: 'nom', label: 'Nom du document', order: 0 },
  { key: 'categorieDate', label: 'Catégorie · date', order: 1 },
];

/** Separator between the category and the date. */
const CATEGORY_DATE_SEPARATOR = ' · ';

/** The display text of a descriptor for a document (`null` parts are omitted). */
export function premiumDocumentFieldText(
  key: PremiumDocumentItemKey,
  document: DocumentItem,
): string {
  if (key === 'nom') return document.nom?.trim() ?? '';
  return [document.categorie?.trim() ?? '', document.date?.trim() ?? '']
    .filter((part) => part !== '')
    .join(CATEGORY_DATE_SEPARATOR);
}

/** Resolve a (possibly overridden) title template with the premium reference. */
export function resolvePanelTitle(template: string, numQui: string | null): string {
  return template.split(NUM_QUI_TOKEN).join(numQui?.trim() ?? '').trim();
}
